import React, { ReactElement, memo } from 'react';
import { Button, Row, Cell, Grid, FormControl, colors } from '@dexma/ui-components';
import i18n from 'asset/translation';
import gtmIds from 'adapter/in/dashboards/config/gtm';
import ServerErrorAlert from 'adapter/in/dashboards/components/ServerErrorAlert';
import { Control, Controller } from 'react-hook-form';
import { StyledDuplicateDashboardModal } from 'adapter/in/dashboards/pages/Modals/DuplicateModal/style';
import { DASHBOARD_NAME_MAX_LENGTH } from 'adapter/in/dashboards/pages/Modals/DashboardNameModal';

const gtm = gtmIds.dashboard.duplicateModal;

export type DuplicateDashboardModalProps = {
    control: Control<{ name: string }>;
    defaultValue: string;
    error: boolean;
    success: boolean;
    isLoading: boolean;
    showModal: boolean;
    onClose: () => void;
    handleOnSubmit: () => void;
};

const DuplicateDashboardModal = memo(
    (props: DuplicateDashboardModalProps): ReactElement => {
        const { control, defaultValue, handleOnSubmit, onClose, showModal, error, success, isLoading } = props;

        let dataTestId = 'duplicate';
        if (success) dataTestId = 'success';
        if (isLoading) dataTestId = 'loading';

        return (
            <StyledDuplicateDashboardModal
                width={550}
                visible={showModal}
                onCancel={onClose}
                title={i18n.t('dashboards.duplicate.modal.title')}
            >
                <form
                    data-testid='duplicate-dashboard-modal-form'
                    defaultValue={defaultValue}
                    onSubmit={(e: React.SyntheticEvent<HTMLFormElement>) => {
                        e.preventDefault();
                        handleOnSubmit();
                    }}
                >
                    <Grid fluid className='no-padding-grid' data-testid='duplicate-dashboard-modal'>
                        <Row>
                            {error && (
                                <Cell>
                                    <ServerErrorAlert tryAgain={() => handleOnSubmit()} dataId={`$${gtm.actions.tryAgain}`} />
                                </Cell>
                            )}
                        </Row>
                        <Row>
                            <Cell
                                style={{
                                    color: colors.gray700,
                                    fontWeight: 600,
                                    lineHeight: '16px',
                                    fontSize: '14px',
                                    paddingBottom: '5px',
                                }}
                            >
                                <span>{i18n.t('dashboards.duplicate.modal.name')}</span>
                            </Cell>
                        </Row>
                        <Row>
                            <Cell>
                                <Controller
                                    control={control}
                                    rules={{
                                        required: { value: true, message: i18n.t('dashboards.create.noNameProvided') },
                                        maxLength: { value: DASHBOARD_NAME_MAX_LENGTH, message: i18n.t('dashboards.create.nameTooLong') },
                                    }}
                                    defaultValue={defaultValue}
                                    name='name'
                                    render={({ field: { onChange, onBlur, value }, fieldState: { error: fieldError } }) => (
                                        <FormControl
                                            data-testid='input-name'
                                            control='Input'
                                            onChange={onChange}
                                            onBlur={onBlur}
                                            disabled={isLoading || success}
                                            value={value ?? defaultValue}
                                            message={fieldError?.message}
                                            error={fieldError}
                                            dataId={gtm.inputName}
                                        />
                                    )}
                                />
                            </Cell>
                        </Row>
                        <div className='buttons'>
                            <Button
                                variant='primary'
                                type='submit'
                                text={i18n.t('dashboards.duplicate.action.duplicateShort')}
                                data-testid={dataTestId}
                                isLoading={isLoading}
                                dataId={gtm.actions.duplicate}
                            />
                            <Button
                                className='cancel'
                                variant='secondary'
                                text={i18n.t('common.cancel')}
                                type='button'
                                data-testid='cancel'
                                onClick={() => {
                                    onClose();
                                }}
                                dataId={gtm.actions.cancel}
                            />
                        </div>
                    </Grid>
                </form>
            </StyledDuplicateDashboardModal>
        );
    }
);

export default DuplicateDashboardModal;
